import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Loading, ScalrEmptyBox } from '@/components/customComponents'
import { ScalrBoxAccordionView } from '@/components/customComponents/scalrBox'

import {
  useCategoryAlerts,
  useSalesLensCategories,
  useSalesLensItems,
} from '@/hooks'
import { useReduxBaseFilter } from '@/store'
import { IAbstractLensItem } from '@/types'
import { dateToString } from '@/utils'

const SalesLensPage = () => {
  const navigate = useNavigate()

  // Get filter state from Redux (base filter - no temperature/size on main page)
  const { selectedBranch, date, dateFilterType, refreshTrigger } =
    useReduxBaseFilter()

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const { getCategoryAlertCount, getItemAlertCount } =
    useCategoryAlerts('Sales Lens')

  const {
    formattedCategoriesData,
    loading,
    refetch: categoriesRefetch,
  } = useSalesLensCategories(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    dateFilterType,
    selectedBranch
  )

  const {
    fetchItemsForCategory,
    hasItemsForCategory,
    getFormattedItemsForCategory,
    loading: itemsLoading,
  } = useSalesLensItems(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    dateFilterType,
    selectedBranch
  )

  // Watch for refresh trigger from Redux
  useEffect(() => {
    if (refreshTrigger['sales-lens']) {
      console.log('Refreshing all sales lens data...')
      setSelectedCategory(null)
      categoriesRefetch()
    }
  }, [refreshTrigger, categoriesRefetch])

  useEffect(() => {
    setSelectedCategory(null)
  }, [selectedBranch, date])

  const displayData = useMemo(() => {
    const result: Array<IAbstractLensItem<any>> = []

    formattedCategoriesData.forEach((category) => {
      result.push({ ...category })

      if (selectedCategory === category.item) {
        const formattedItems = getFormattedItemsForCategory(category.item)
        result.push(...formattedItems)
      }
    })

    return result
  }, [formattedCategoriesData, selectedCategory, getFormattedItemsForCategory])

  const handleCategoryClick = useCallback(
    async (item: IAbstractLensItem<any>) => {
      const categoryName = item.item
      if (!categoryName) return

      // If the item has a parentName, it's an item (child), not a category
      const isActualItem = !!item.parentName

      const isParentCategory =
        formattedCategoriesData.some((cat) => cat.item === categoryName) &&
        !isActualItem

      if (!isParentCategory) {
        return
      }

      if (
        selectedCategory === categoryName &&
        hasItemsForCategory(categoryName)
      ) {
        setSelectedCategory(null)
        return
      }

      try {
        await fetchItemsForCategory(categoryName)
        setSelectedCategory(categoryName)
      } catch (error) {
        console.error('Error fetching items:', error)
        // Handle error appropriately - could show a toast notification
      }
    },
    [
      selectedCategory,
      formattedCategoriesData,
      fetchItemsForCategory,
      hasItemsForCategory,
    ]
  )

  const handleItemClick = useCallback((item: IAbstractLensItem<any>) => {
    // TODO: check better way to do this
    localStorage.setItem('categoryItem', item.parentName || '')
    navigate(`/clarity-lens/sales-lens/${item.item}`)
  }, [])

  return (
    <div className="space-y-4">
      {loading && displayData.length === 0 ? (
        <Loading isContainer />
      ) : displayData.length === 0 ? (
        <ScalrEmptyBox />
      ) : (
        <ScalrBoxAccordionView
          data={displayData}
          onCategoryClick={handleCategoryClick}
          onItemClick={handleItemClick}
          selectedItem={selectedCategory}
          loading={loading || itemsLoading}
          getCategoryAlertCount={getCategoryAlertCount}
          getSubItemAlertCount={getItemAlertCount}
        />
      )}
    </div>
  )
}

export default SalesLensPage
